import type { PasskeyHintStatus } from './evaluateAuthMethod';

const HINT_STORAGE_KEY = 'passkey_device_hint_v1';

type HintMap = Record<string, 'present' | 'absent'>;

export const normalizePasskeyUsername = (username: string): string =>
  (username || '').trim().toLowerCase();

const bytesToHex = (bytes: ArrayBuffer): string =>
  Array.from(new Uint8Array(bytes))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

const hashUsername = async (username: string): Promise<string | null> => {
  const normalized = normalizePasskeyUsername(username);
  if (!normalized) {
    return null;
  }
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const data = new TextEncoder().encode(normalized);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return bytesToHex(digest);
  }
  return `fallback:${normalized}`;
};

const readHintMap = (): HintMap => {
  try {
    if (typeof localStorage === 'undefined') {
      return {};
    }
    const raw = localStorage.getItem(HINT_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return {};
    }
    return parsed as HintMap;
  } catch (error) {
    console.warn('[Passkey] Failed to read device hint cache:', error);
    return {};
  }
};

const writeHintMap = (map: HintMap): void => {
  try {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(HINT_STORAGE_KEY, JSON.stringify(map));
  } catch (error) {
    console.warn('[Passkey] Failed to write device hint cache:', error);
  }
};

export const getPasskeyHintStatus = async (
  username: string
): Promise<PasskeyHintStatus> => {
  const hash = await hashUsername(username);
  if (!hash) {
    return 'unknown';
  }
  const map = readHintMap();
  return map[hash] ?? 'unknown';
};

export const setPasskeyHintPresent = async (username: string): Promise<void> => {
  const hash = await hashUsername(username);
  if (!hash) {
    return;
  }
  const map = readHintMap();
  map[hash] = 'present';
  writeHintMap(map);
};

export const setPasskeyHintAbsent = async (username: string): Promise<void> => {
  const hash = await hashUsername(username);
  if (!hash) {
    return;
  }
  const map = readHintMap();
  map[hash] = 'absent';
  writeHintMap(map);
};
